import { firestore, auth } from '@/shared/lib/firebase'
import { LocalUser } from '@/shared/lib/db'
import { doc, getDoc } from 'firebase/firestore'
import axiosClient from '@/features/shared/http/axios_client'

export const FILMMAKING_EXPERTISE_LEVELS = [
    {
        value: 'beginner',
        label: 'Beginner',
        description: 'Just getting started with filmmaking.',
    },
    {
        value: 'intermediate',
        label: 'Intermediate',
        description: 'Comfortable with the basics and building real projects.',
    },
    {
        value: 'professional',
        label: 'Professional',
        description: 'Working at an advanced or industry level.',
    },
] as const

export type FilmmakingExpertise = (typeof FILMMAKING_EXPERTISE_LEVELS)[number]['value']

export type UserProfile = {
    uid: string
    email: string | null
    displayName?: string
    photoURL?: string
    name?: string
    filmmakingExpertise?: FilmmakingExpertise
    onboardingCompleted: boolean
}

type CompleteOnboardingPayload = {
    name?: string
    filmmakingExpertise: FilmmakingExpertise
}

export async function getUserProfile(uid: string): Promise<Partial<UserProfile> | null> {
    const userProfileRef = doc(firestore, 'users', uid)
    const userProfileSnapshot = await getDoc(userProfileRef)

    if (!userProfileSnapshot.exists()) {
        return null
    }

    return userProfileSnapshot.data() as Partial<UserProfile>
}

export function buildLocalUser(
    firebaseUser: {
        uid: string
        email: string | null
        displayName: string | null
        photoURL: string | null
    },
    profile: Partial<UserProfile> | null
): LocalUser {
    const fallbackName = firebaseUser.displayName ?? undefined
    const profileName = profile?.name?.trim()
    const effectiveName = profileName || fallbackName

    return {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName ?? undefined,
        photoURL: firebaseUser.photoURL ?? undefined,
        name: effectiveName,
        filmmakingExpertise: profile?.filmmakingExpertise,
        onboardingCompleted:
            Boolean(profile?.onboardingCompleted) &&
            Boolean(profile?.filmmakingExpertise) &&
            Boolean(effectiveName),
    }
}

export async function completeOnboarding(payload: CompleteOnboardingPayload) {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('Not authenticated');
    }

    const token = await user.getIdToken();
    const response = await axiosClient.post<{ data: UserProfile }>(
        '/completeUserOnboarding',
        payload,
        {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }
    );
    
    return response.data.data;
}


export function getPostAuthRedirectPath(user: LocalUser | null) {
    if (!user) {
        return '/login'
    }

    return user.onboardingCompleted ? '/dashboard' : '/onboarding'
}
